import { useParams, useSearchParams } from 'react-router-dom';
import { MOTIVO_LABEL, originColor, painelApi } from '@/api/painel';
import { ErrorBox, EstimatedTag } from '@/components/painel/Kpi';
import { PeriodPicker } from '@/components/painel/PeriodPicker';
import { useLoad, usePainelPeriod } from '@/components/painel/PainelContext';
import { fmtBrl, fmtDateTime, fmtDuration, fmtInt, fmtKwh } from '@/components/painel/format';

const PAGE_SIZE = 25;

/**
 * Recargas do período, sem dado pessoal do motorista: o cliente do app Bow
 * aparece como pseudônimo ("Cliente #a3f2b1"), roaming não identifica ninguém.
 */
export default function PainelSessoes() {
  const { orgId = '' } = useParams();
  const [period, setPeriod] = usePainelPeriod();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const key = JSON.stringify(period);
  const s = useLoad(() => painelApi.sessions(orgId, period, page, PAGE_SIZE), [orgId, key, page]);

  const pages = s.data ? Math.max(1, Math.ceil(s.data.total / PAGE_SIZE)) : 1;
  const go = (p: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next, { replace: true });
  };

  return (
    <div className="pt-1">
      <PeriodPicker value={period} onChange={setPeriod} label={s.data?.period.label} />
      {s.error && !s.data && <ErrorBox message={s.error} onRetry={s.reload} />}
      {s.data && (
        <div className={s.loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <p className="m-0 mt-3 text-[13px] text-bow-royal/60">{fmtInt(s.data.total)} recargas concluídas</p>
          {s.data.items.length === 0 && (
            <div className="mt-3 rounded-2xl bg-white p-5 text-[14px] text-bow-royal/70">
              Nenhuma recarga no período.
            </div>
          )}
          <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
            {s.data.items.map((it, i) => (
              <li key={`${it.inicio}-${i}`} className="rounded-2xl bg-white px-4 py-3 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[14px] font-semibold">
                      <span
                        aria-hidden
                        style={{ width: 8, height: 8, borderRadius: 2, background: originColor(it.origem) }}
                      />
                      {it.origem_label}
                      {it.cliente && <span className="font-medium text-bow-royal/55">· {it.cliente}</span>}
                    </div>
                    <div className="mt-0.5 text-[12px] text-bow-royal/55">
                      {fmtDateTime(it.inicio)} · {fmtDuration(it.duracao_min)} · {it.carregador}
                      {it.conector != null && ` · conector ${it.conector}`}
                    </div>
                    {it.motivo_fim !== 'outro' && (
                      <div className="mt-0.5 text-[11.5px] text-bow-royal/45">{MOTIVO_LABEL[it.motivo_fim]}</div>
                    )}
                  </div>
                  <div className="shrink-0 text-right tabular-nums">
                    <div className="text-[15px] font-semibold">
                      {fmtBrl(it.valor_brl)}
                      {it.valor_estimado && <EstimatedTag />}
                    </div>
                    <div className="text-[12px] text-bow-royal/55">{fmtKwh(it.kwh)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <nav aria-label="Páginas" className="mt-4 flex items-center justify-between text-[13px]">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => go(page - 1)}
                className="rounded-full bg-white px-4 py-2 font-semibold disabled:opacity-40"
              >
                ← Anteriores
              </button>
              <span className="text-bow-royal/55">
                Página {page} de {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => go(page + 1)}
                className="rounded-full bg-white px-4 py-2 font-semibold disabled:opacity-40"
              >
                Próximas →
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}
